type ToolEventInputs = {
  kind: string
  toolName: string
  category: string
  provider?: string | null
}

export type ResolvedIcon = { key: string; color?: string }

export type EventIcon = {
  kind: 'registry' | 'agent'
  key?: string
  name?: string
  style?: string
  color?: string
}

const KNOWN_PROVIDERS = new Set([
  'salesforce', 'leadiq', 'pipedrive', 'hubspot', 'gong',
  'openai', 'anthropic', 'exa',
])

function integrationKeyForProvider(provider?: string | null): string | null {
  const prov = String(provider || '').trim().toLowerCase()
  return KNOWN_PROVIDERS.has(prov) ? prov : null
}

const CATEGORY_TO_ICON: Record<string, ResolvedIcon> = {
  salesforce: { key: 'salesforce' },
  leadiq:     { key: 'leadiq' },
  pipedrive:  { key: 'pipedrive' },
  hubspot:    { key: 'hubspot' },
  reachout:   { key: 'reachout' },
  reachouts:  { key: 'reachout' },
  task:       { key: 'task' },
  tasks:      { key: 'task' },
  followup:   { key: 'followup' },
  followups:  { key: 'followup' },
  navigate:   { key: 'footprints', color: '#0d9488' },
  // Results already fetched, read back offline.
  salesforce_offline: { key: 'salesforce_offline', color: '#01354f' },
  leadiq_offline:     { key: 'leadiq_offline', color: '#9ca3af' },
}

// Tool-name backstops for emit sites that don't yet set ``category``.
const LEGACY_NAME_TO_INTEGRATION: Record<string, string> = {
  fetch_salesforce_account:      'salesforce',
  fetch_salesforce_opportunities: 'salesforce',
  fetch_salesforce_contacts:     'salesforce',
  fetch_salesforce_emails:       'salesforce',
  fetch_pipedrive_organization:  'pipedrive',
  fetch_pipedrive_persons:       'pipedrive',
  fetch_pipedrive_leads:         'pipedrive',
  fetch_pipedrive_deals:         'pipedrive',
  fetch_pipedrive_activities:    'pipedrive',
  fetch_pipedrive_mails:         'pipedrive',
  fetch_hubspot_company:         'hubspot',
  fetch_hubspot_contacts:        'hubspot',
  fetch_hubspot_engagements:     'hubspot',
  fetch_hubspot_emails:          'hubspot',
  fetch_hubspot_emails_for_contact: 'hubspot',
  find_prospects:                'leadiq',
  enrich_prospect:               'leadiq',
  get_prospect_talking_points:    'leadiq',
}

const LEGACY_NAME_TO_ICON: Record<string, ResolvedIcon> = {
  map_prospect_past_positions_to_customers: { key: 'customer_list' },
  map_company_to_similar_customers:         { key: 'customer_list' },
  emit_signal:                              { key: 'signal' },
  manage_followups:                         { key: 'followup' },
}

const RAVEN: EventIcon = { kind: 'registry', key: 'raven' }
const CANCEL_REQUESTED: EventIcon = { kind: 'registry', key: 'cancel_requested', color: '#dc2626' }

function resolveLegacyName(toolName: string): ResolvedIcon | null {
  if (!toolName) return null
  const integration = LEGACY_NAME_TO_INTEGRATION[toolName]
  if (integration) return { key: integration }
  const direct = LEGACY_NAME_TO_ICON[toolName]
  if (direct) return direct
  if (toolName.startsWith('emit_') && toolName.includes('signal')) {
    return { key: 'signal' }
  }
  return null
}

const RAVEN_SYSTEM_MESSAGES = new Set([
  'run queued.',
  'worker picked up the run.',
  'run completed.',
  'run stopped',
  'run re-queued by stale run detector.',
])

const RAVEN_CONTEXT_KEYS = new Set([
  'about_us',
  'icp_definition',
  'account:icp_score',
  'account:contacts',
  'account:buying_team',
  'account:timeline',
  'account:sequences',
  'signals',
  'account:tasks',
  'account:followups',
  'account:reachouts',
  'agent_runtime_user',
  'run_restart',
])

const TERMINAL_MESSAGES = new Set([
  'run completed.', 'run stopped', 'run failed', 'run cancelled',
])

type RawEvent = {
  kind?: string | null
  message?: string | null
  payload?: Record<string, any> | null
}

function kindOf(ev: RawEvent): string {
  return String(ev?.kind || '').trim().toLowerCase()
}

function toolNameOf(ev: RawEvent): string {
  const name = ev?.payload?.tool?.name
  return typeof name === 'string' ? name.trim() : ''
}

export function isRestartEvent(ev: RawEvent): boolean {
  return kindOf(ev) === 'system_log' && ev?.payload?.context === 'run_restart'
}

export function isCancelRequestedEvent(ev: RawEvent): boolean {
  if (kindOf(ev) !== 'system_log') return false
  const msg = String(ev?.message || '').trim().toLowerCase()
  return msg === 'cancel requested.' || msg === 'cancel requested'
}

export function isTerminalEvent(ev: RawEvent): boolean {
  const msg = String(ev?.message || '').trim().toLowerCase()
  for (const t of TERMINAL_MESSAGES) {
    if (msg.startsWith(t)) return true
  }
  return false
}

function isRavenEvent(ev: RawEvent): boolean {
  const kind = kindOf(ev)
  if (kind === 'agent_config_snapshot') return true
  if (kind === 'system_log') {
    const logVersion = Number(ev?.payload?.agent_log_version || 0) || 0
    if (logVersion > 0) return true
    const msg = String(ev?.message || '').trim().toLowerCase()
    if (RAVEN_SYSTEM_MESSAGES.has(msg)) return true
    if (msg.startsWith('run stopped')) return true
    if (msg.startsWith('context ready for agent')) return true
    const ctx = String(ev?.payload?.context || '').toLowerCase()
    if (ctx && RAVEN_CONTEXT_KEYS.has(ctx)) return true
  }
  return false
}

function resolveToolEventIcon(ev: ToolEventInputs): ResolvedIcon {
  if (String(ev.kind || '').toLowerCase() !== 'tool') return { key: 'raven' }

  const providerKey = integrationKeyForProvider(ev.provider)
  if (providerKey) return { key: providerKey }

  const cat = String(ev.category || '').trim().toLowerCase()
  if (cat && CATEGORY_TO_ICON[cat]) return CATEGORY_TO_ICON[cat]

  const name = String(ev.toolName || '').trim()
  const legacyHit = resolveLegacyName(name)
  if (legacyHit) return legacyHit

  return { key: 'raven' }
}

function resolveSystemLogIntegration(args: {
  provider?: string | null
  context?: string | null
}): ResolvedIcon | null {
  const providerKey = integrationKeyForProvider(args.provider)
  if (providerKey) return { key: providerKey }

  const ctx = String(args.context || '').trim().toLowerCase()
  if (!ctx) return null
  if (ctx === 'gong' || ctx.startsWith('gong:')) return { key: 'gong' }
  if (ctx === 'salesforce' || ctx.startsWith('salesforce:')) return { key: 'salesforce' }
  if (ctx === 'pipedrive' || ctx.startsWith('pipedrive:')) return { key: 'pipedrive' }
  if (ctx === 'hubspot' || ctx.startsWith('hubspot:')) return { key: 'hubspot' }
  if (ctx === 'leadiq' || ctx.startsWith('leadiq:')) return { key: 'leadiq' }
  if (ctx === 'outreach' || ctx.startsWith('outreach:')) return { key: 'outreach' }
  return null
}

function agentIcon(
  name: string | null | undefined,
  style?: string | null,
  color?: string | null,
): EventIcon | null {
  if (!name) return null
  return {
    kind: 'agent',
    name: String(name),
    style: String(style || 'outline'),
    color: String(color || '#6366f1'),
  }
}

export type RootAgent = {
  name?: string | null
  style?: string | null
  color?: string | null
}

export function computeAgentIconStack(
  events: RawEvent[],
  root: RootAgent,
): (EventIcon | null)[] {
  const map: (EventIcon | null)[] = new Array(events.length)
  const rootIcon = agentIcon(root.name, root.style, root.color)
  const stack: { spanId: string; icon: EventIcon | null }[] = [{ spanId: '', icon: rootIcon }]

  for (let i = 0; i < events.length; i += 1) {
    const ev = events[i]

    if (isRestartEvent(ev)) {
      while (stack.length > 1) stack.pop()
    }

    const kind = kindOf(ev)
    const toolName = toolNameOf(ev)
    const category = String(ev?.payload?.tool?.category || '').toLowerCase()
    const phase = String(ev?.payload?.tool?.phase || '').toLowerCase()
    const isAgentTool = kind === 'tool' && (toolName === 'call_agent' || category === 'agent_tools')

    if (isAgentTool && phase === 'start') {
      const spanId = typeof ev?.payload?.span_id === 'string' ? ev.payload.span_id.trim() : ''
      if (spanId) {
        stack.push({
          spanId,
          icon: agentIcon(
            ev?.payload?.agent_icon_name,
            ev?.payload?.agent_icon_style,
            ev?.payload?.agent_icon_color,
          ),
        })
      }
    }

    map[i] = stack.length ? stack[stack.length - 1].icon : null

    if (isAgentTool && phase === 'done') {
      const spanId = typeof ev?.payload?.span_id === 'string' ? ev.payload.span_id.trim() : ''
      if (spanId) {
        for (let j = stack.length - 1; j > 0; j -= 1) {
          if (stack[j].spanId === spanId) {
            stack.length = j
            break
          }
        }
      }
    }
  }

  return map
}

export function resolveEventIcon(args: {
  event: RawEvent
  index?: number | null
  agentStack?: (EventIcon | null)[] | null
  root?: RootAgent
}): EventIcon {
  const ev = args.event
  const root = args.root || {}
  const kind = kindOf(ev)

  if (kind === 'tool') {
    const icon = resolveToolEventIcon({
      kind,
      toolName: toolNameOf(ev),
      category: String(ev?.payload?.tool?.category || '').toLowerCase(),
      provider: String(ev?.payload?.tool?.provider || '').toLowerCase(),
    })
    if (icon.key !== 'raven') {
      return { kind: 'registry', key: icon.key, color: icon.color }
    }
    // Fall through so the agent-stack lookup below can pick up an in-flight agent's icon.
  }

  if (ev?.payload?.__synthetic_agent_boundary) {
    return (
      agentIcon(ev?.payload?.agent_icon_name, ev?.payload?.agent_icon_style, ev?.payload?.agent_icon_color) ||
      agentIcon(root.name, root.style, root.color) ||
      RAVEN
    )
  }

  if (isCancelRequestedEvent(ev)) return CANCEL_REQUESTED
  if (isRavenEvent(ev)) return RAVEN
  if (isTerminalEvent(ev)) return RAVEN

  if (kind === 'system_log') {
    const branded = resolveSystemLogIntegration({
      provider: ev?.payload?.provider,
      context: String(ev?.payload?.context || '').toLowerCase(),
    })
    if (branded) return { kind: 'registry', key: branded.key, color: branded.color }
  }

  const idx = typeof args.index === 'number' ? args.index : -1
  const stacked = idx >= 0 && args.agentStack ? args.agentStack[idx] : null
  if (stacked) return stacked

  return agentIcon(root.name, root.style, root.color) || RAVEN
}
